import {
  CondenserType,
  ConfigurationType,
  SystemOptionType,
  ZoneType
} from 'data/types';
import {
  readCompatibilityPairs,
  readDesignConfigurationUniqueId
} from 'utils/compatibility-bundle-fields';
import { CompatibilityPairRef } from 'utils/compatibility-bundle-types';
import { getDuctedPairZone } from 'utils/ducted-design';
import { isDuctedDesignConfiguration } from 'utils/ducted-design-predicates';
import { WholeHouseDuctedConfigurationType } from 'utils/whole-house-ducted-configuration-types';

type QuotedCondenser = SystemOptionType['configuration']['condensers'][number];
type QuotedZone = QuotedCondenser['zones'][number];

export type BuildDuctedDesignLayoutParams = {
  system: SystemOptionType;
  configurations: ConfigurationType[];
};

export function resolveDesignConfiguration(
  system: SystemOptionType,
  configurations: ConfigurationType[]
): ConfigurationType | null {
  const designUniqueId = readDesignConfigurationUniqueId(system);
  if (!designUniqueId) return null;
  const design = configurations.find(
    (configuration) => configuration.uniqueId === designUniqueId
  );
  return design && isDuctedDesignConfiguration(design) ? design : null;
}

function layoutZone(
  designZone: ZoneType,
  quotedZone: QuotedZone,
  pair: CompatibilityPairRef
): ZoneType {
  return {
    ...designZone,
    uniqueId: quotedZone.uniqueId,
    name: quotedZone.name,
    type: 'ducted',
    existingSystemId:
      pair.existingSystemUniqueId ?? designZone.existingSystemId,
    ...(quotedZone.selectedProduct
      ? { selectedProduct: quotedZone.selectedProduct }
      : {}),
    modified: false
  };
}

function layoutCondenser(
  designCondenser: CondenserType,
  quotedCondenser: QuotedCondenser,
  pair: CompatibilityPairRef
): CondenserType | null {
  const designZone = getDuctedPairZone(designCondenser);
  const [quotedZone] = quotedCondenser.zones;
  if (!designZone || !quotedZone) return null;
  return {
    ...designCondenser,
    uniqueId: quotedCondenser.uniqueId,
    name: quotedCondenser.name,
    type: 'ducted',
    ...(quotedCondenser.selectedProduct
      ? { selectedProduct: quotedCondenser.selectedProduct }
      : {}),
    zones: [layoutZone(designZone, quotedZone, pair)],
    validSystems: [],
    validDuctedSystems: [],
    ductedAccessories: [],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    modified: false
  };
}

export function buildDuctedDesignLayoutConfiguration({
  system,
  configurations
}: BuildDuctedDesignLayoutParams): WholeHouseDuctedConfigurationType | null {
  const design = resolveDesignConfiguration(system, configurations);
  if (!design) return null;
  const pairs = readCompatibilityPairs(system);
  if (pairs.length === 0) return null;

  const condensers = system.configuration.condensers.map((quotedCondenser) => {
    const pair = pairs.find(
      (candidate) => candidate.condenserUniqueId === quotedCondenser.uniqueId
    );
    const designCondenser = design.condensers.find(
      (candidate) => candidate.uniqueId === pair?.designCondenserUniqueId
    );
    if (!pair || !designCondenser) return null;
    return layoutCondenser(designCondenser, quotedCondenser, pair);
  });
  if (condensers.some((condenser) => condenser === null)) return null;

  return {
    uniqueId: system.configuration.uniqueId,
    name: `${system.name} Layout`,
    type: 'ducted',
    condensers: condensers as CondenserType[],
    valid: true,
    modified: false,
    layOutDuctedZones: true
  };
}
